'use client';

import { type ComponentPropsWithoutRef, type ElementType } from 'react';

import { cn, motion, tv } from '@/libs';

import { Icon as IconComponent, type IconName } from '../Icon';

type Variant = 'primary' | 'secondary' | 'tertiary' | 'outlined' | 'ghost' | 'link';
type Size = 'small' | 'regular' | 'large';

type ButtonCoreProps = {
  children?: React.ReactNode;
  loading?: boolean;
  loadingText?: string;
  variant?: Variant;
  size?: Size;
  icon?: IconName;
  disabled?: boolean;
  as?: ElementType;
};

type ButtonProps<E extends ElementType = 'button'> = {
  as?: E;
} & ButtonCoreProps &
  Omit<ComponentPropsWithoutRef<E>, keyof ButtonCoreProps | 'as'>;

export const buttonVariants = tv({
  base: 'inline-flex items-center gap-1 justify-center rounded cursor-pointer transition-all focus:outline-none text-foreground outline-none transition-colors duration-200 ring-offset-background ring-foreground focus-visible:transition-all focus-visible:ring-2 focus-visible:ring-offset-2 hover:opacity-90',
  variants: {
    variant: {
      primary: 'uppercase bg-primary sm:hover:bg-primary-hover text-primary-foreground',
      secondary:
        'uppercase bg-gradient-to-l from-secondary-variant to-secondary-variant/80 sm:hover:bg-secondary-hover text-secondary-foreground',
      tertiary:
        'uppercase bg-gradient-to-l from-tertiary-variant to-tertiary sm:hover:bg-tertiary-hover text-tertiary-foreground',
      outlined:
        'uppercase bg-transparent border-2 border-foreground text-foreground sm:hover:text-background sm:hover:bg-foreground',
      ghost: 'uppercase bg-transparent text-foreground sm:hover:bg-foreground/10',
      link: 'bg-transparent text-foreground h-auto !px-0 !font-medium sm:hover:text-secondary-text',
    },
    size: {
      small: 'text-xs font-medium px-2 min-w-6 md:min-w-8 h-6 md:h-8',
      regular: 'text-xs md:text-sm font-semibold px-2 min-w-8 md:min-w-9 h-8 md:h-9',
      large: 'text-base font-semibold px-4 min-w-9 md:min-w-10 h-10 md:h-11',
    },
    disabledVariant: {
      primary: '!bg-foreground/10 cursor-not-allowed',
      secondary: '!bg-foreground/10 cursor-not-allowed',
      tertiary: '!bg-foreground/10 cursor-not-allowed',
      outlined: '!bg-transparent border-foreground/10 cursor-not-allowed',
      ghost: '!bg-transparent cursor-not-allowed',
      link: 'pointer-events-none',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'regular',
  },
});

export const Button = <E extends ElementType = 'button'>({
  as,
  children,
  loading = false,
  loadingText = 'Loading',
  variant = 'primary',
  size = 'regular',
  icon,
  disabled,
  className,
  ...props
}: ButtonProps<E>) => {
  const isDisabled = disabled || loading;

  const Icon = icon ? IconComponent[icon] : null;

  const Component = (as ?? 'button') as ElementType;

  const commonProps = {
    className: cn(
      buttonVariants({
        variant,
        size,
        disabledVariant: isDisabled ? variant : undefined,
      }),
      className,
      icon && 'aspect-square p-0',
    ),
    'aria-disabled': isDisabled,
    'aria-busy': loading,
    disabled,
    ...props,
  } as React.ComponentPropsWithoutRef<E>;

  return (
    <Component {...commonProps}>
      {loading ? (
        <>
          <motion.div
            className="w-4 h-4 border-2 border-b-foreground/20 border-x-foreground/20 border-t-foreground/60 rounded-full"
            initial={{ rotate: 0 }}
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 0.5, ease: 'linear' }}
          />
          {loadingText && <span>{loadingText}</span>}
        </>
      ) : icon && Icon ? (
        typeof Icon === 'function' ? (
          <Icon width={size === 'small' ? 16 : 20} height={size === 'small' ? 16 : 20} />
        ) : (
          Icon
        )
      ) : (
        children
      )}
    </Component>
  );
};
